/**
 * Strip tabs — ONE bottom-left slot shared by the Pages and States strips.
 *
 * Both contexts previously competed for screen space (states frame label top,
 * pages strip top-center, both openable at once — unclear which context edits
 * route to). This coordinator makes the context explicit and singular:
 *
 *  - A PERSISTENT tab bar sits bottom-left — one tab per available context
 *    ("Pages", "States"). Open, the bar rides INSIDE the strip panel as its
 *    footer (the panel background encompasses the tabs); closed, it stands
 *    alone padded to the same offsets — so each tab stays in the same spot.
 *    Clicking a tab opens its strip, the active tab (or ✕) closes, the other
 *    tab swaps. The viewport context frame renders at the slot level here
 *    (not inside the strip) so it spans the full canvas area.
 *
 * Exactly one strip is visible at a time, so exactly one viewport frame and
 * one capture mode is active: opening a tab calls the adapter's
 * onToggleEnabled(true) (states → states-editing mode, pages → page-editing
 * mode) and swapping/closing calls onToggleEnabled(false) on the outgoing
 * one. The mounts (mount-state-context / mount-page-context) publish their
 * adapters here instead of rendering their own strips; publishing null
 * withdraws a context (its tab disappears).
 *
 * Singleton — both mounts publish into the same host across scene reloads.
 */

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { observeInspectorPaneInsets } from './inspector-panes';
import { PoseStrip, type PoseAdapter } from './pose-strip';

export type StripTabId = 'pages' | 'states';

const TAB_LABELS: Record<StripTabId, { glyph: string; label: string }> = {
    pages: { glyph: '⧉', label: 'Pages' },
    states: { glyph: '◇', label: 'States' },
};

/** Fixed display order: Pages (document level) before States (object level). */
const TAB_ORDER: StripTabId[] = ['pages', 'states'];

export interface StripTabsHost {
    /** Publish (or withdraw with null) a context's adapter. */
    publish(tab: StripTabId, adapter: PoseAdapter | null): void;
    /** Open a tab / close (null) — same path the UI buttons use. */
    setOpen(tab: StripTabId | null): void;
    getOpen(): StripTabId | null;
}

/** The persistent tab bar — identical whether a strip is open or not, so the
 *  tabs never jump when toggled. The active tab is tinted; clicking it closes. */
function TabRow({ tabs, open, onToggle }: {
    tabs: { id: StripTabId }[];
    open: StripTabId | null;
    onToggle: (id: StripTabId) => void;
}) {
    return (
        <div style={{ display: 'flex', gap: '4px', pointerEvents: 'auto', userSelect: 'none' }}>
            {tabs.map((t) => {
                const active = t.id === open;
                const { glyph, label } = TAB_LABELS[t.id];
                return (
                    <button
                        key={t.id}
                        className="pose-edit"
                        onClick={() => onToggle(t.id)}
                        title={active
                            ? `Close ${label} — edits change the base scene`
                            : t.id === 'states'
                                ? 'Edit states — edit and preview states in the viewport. While closed, edits change the base scene.'
                                : 'Open pages — navigate pages; edits to shared objects store on the current page. While closed, edits change the base scene.'}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '7px 12px', borderRadius: '9px',
                            background: active ? 'rgba(20, 30, 46, 0.94)' : 'rgba(18, 20, 26, 0.92)',
                            border: `1px solid ${active ? 'rgba(120, 170, 210, 0.45)' : 'rgba(255, 255, 255, 0.10)'}`,
                            color: active ? '#dbe9f5' : 'rgba(236, 240, 245, 0.55)',
                            cursor: 'pointer', fontSize: '11px',
                            backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                            transition: 'color 0.12s, border-color 0.12s, background-color 0.12s',
                        }}
                    >
                        <span style={{ fontSize: '12px', color: active ? 'rgb(120, 170, 210)' : 'inherit' }}>{glyph}</span>
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

function createHost(): StripTabsHost {
    let boundEl: HTMLElement | null = null;
    let root: Root | null = null;
    let disposePaneObserver: (() => void) | null = null;

    const adapters: Record<StripTabId, PoseAdapter | null> = { pages: null, states: null };
    let openTab: StripTabId | null = null;

    // Re-resolve the container each render: the host is a singleton, but the
    // element can be recreated (inspector re-init, tests remounting the DOM).
    function ensureRoot(): { root: Root; el: HTMLElement } | null {
        const el = document.getElementById('state-context-container');
        if (!el) return null;
        if (el !== boundEl) {
            root?.unmount();
            boundEl = el;
            root = createRoot(el);
        }
        return root ? { root, el } : null;
    }

    // Fit the slot between the inspector sidebars (same observer the other
    // bottom-docked overlays use).
    function setupPaneObserver(containerEl: HTMLElement) {
        disposePaneObserver?.();
        disposePaneObserver = observeInspectorPaneInsets(({ left, right }) => {
            containerEl.style.left = `${left}px`;
            containerEl.style.right = `${right}px`;
        });
    }

    function available(): { id: StripTabId; adapter: PoseAdapter }[] {
        return TAB_ORDER
            .filter((id) => adapters[id] !== null)
            .map((id) => ({ id, adapter: adapters[id]! }));
    }

    function rerender() {
        const bound = ensureRoot();
        if (!bound) return;

        const avail = available();
        if (avail.length === 0) {
            bound.root.render(null);
            return;
        }

        const open = openTab && adapters[openTab] ? openTab : null;
        const adapter = open ? adapters[open]! : null;
        const tabRow = (
            <TabRow
                tabs={avail.map(a => ({ id: a.id }))}
                open={open}
                onToggle={(id) => setOpen(id === open ? null : id)}
            />
        );
        const wrapped: PoseAdapter | null = adapter && {
            ...adapter,
            corner: 'static',
            enabled: true,
            onToggleEnabled: (v) => { if (!v) setOpen(null); },
            // The tab bar rides INSIDE the panel as its footer, so the panel
            // background encompasses the tabs and the content sits close.
            footer: tabRow,
        };

        // Anchored bottom-left, flush with the viewport context frame line
        // (frame inset is 8px). Open: the strip panel carries the tab bar as
        // its footer. Closed: a standalone tab row padded to the footer's
        // exact offsets (panel border 1px + padding 10px/9px), so the tabs
        // never move between states. (The viewport context frame is drawn by
        // PoseStrip itself, portaled to the slot element so it spans the
        // full canvas area — its header carries the pages prev/next nav.)
        bound.root.render(
            <div style={{
                position: 'absolute', bottom: '8px', left: '8px',
                maxWidth: 'calc(100% - 16px)',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            }}>
                {wrapped && open
                    ? <PoseStrip key={`${open}:${wrapped.owner.transform.uniqueId}`} adapter={wrapped} />
                    : <div style={{ padding: '0 0 10px 11px' }}>{tabRow}</div>}
            </div>,
        );
        requestAnimationFrame(() => setupPaneObserver(bound.el));
    }

    function setOpen(tab: StripTabId | null) {
        if (tab === openTab) return;
        const prev = openTab;
        openTab = tab;
        // Deactivate the outgoing mode FIRST (states drains posed contexts back
        // to Base), then activate the incoming one.
        if (prev) adapters[prev]?.onToggleEnabled?.(false);
        if (tab) adapters[tab]?.onToggleEnabled?.(true);
        rerender();
    }

    return {
        publish(tab, adapter) {
            adapters[tab] = adapter;
            rerender();
        },
        setOpen,
        getOpen: () => openTab,
    };
}

let host: StripTabsHost | null = null;

/** The shared strip-tabs host (created on first use). */
export function getStripTabs(): StripTabsHost {
    host ??= createHost();
    return host;
}
